import { createApi} from "@reduxjs/toolkit/query/react";
import customBaseQuery from "./base.api.ts";
import { TicketDetailsResponse } from '../interfaces/ticket.interface.ts'

interface Ticket {
  ticket_id: string;
  ticket_title: string;
  ticket_client: string;
  ticket_category: string;
  ticket_status: string;
  ticket_priority: string;
  createdAt: string;
  closedAt: string | null;
}

export interface CreateTicketInput {
  ticket_title: string;
  ticket_description: string;
  ticket_type_id: number;
  category_type_id: number;
  category_id: number;
  subcategory_id?: number;
  client_id: number;
  company_id: number;
}

interface CreateTicketResponse {
  message: string;
  ticket: {
    ticket_id: number;
    company: string,
    ticket_title: string,
    ticket_description: string,
    createdAt: string,
    ticket_type: string,
    category: string,
    subcategory: string,
    status: string,
    client_username: string,
    client_email: string,
    created_by_agent_username: string,
    priority: string
  };
  resolution: string;
}

export const ticketsApi = createApi({
  reducerPath: "ticketApi",
  baseQuery: customBaseQuery, 
  tagTypes: ['Tickets', 'Ticket', 'PendingTickets'],
  endpoints: (builder) => ({
    //* GET ALL TICKETS
    getAllTickets: builder.query<Ticket[], void>({
      query: () => ({
        url: "/gw/tickets/agent/all",
        method: "get",
      }),
      transformResponse: (response: any) => {
        return response.map((ticket: any) => ({
          ticket_id: ticket.ticket_id.toString(),
          ticket_title: ticket.ticket_title,
          ticket_client: ticket.client.client_username,
          ticket_category: ticket.category,
          ticket_status: ticket.status,
          ticket_priority: ticket.priority_name,
          createdAt: ticket.createdAt,
          closedAt: ticket.closed_at,
        }));
      },
      providesTags: ['Tickets'],
    }),
    //* CREATE TICKET
    //! any: interface for response
    createTicket: builder.mutation<CreateTicketResponse, CreateTicketInput>({
      query: (body) => ({
        url: "/gw/tickets/create/agent",
        method: "POST",
        body,
      }),
      invalidatesTags: ['Tickets', 'Ticket']
    }),
    //* Assign TICKET
    //! any: interface for response
    assignTicket: builder.mutation<any, { ticketId: number, assignedAgentId: number }>({
      query: ({ ticketId, assignedAgentId }) => ({
        url: `/gw/tickets/${ticketId}/assign`,
        method: "PUT",
        body: { assigned_agent_id: assignedAgentId }
      }),
      invalidatesTags: ['Ticket']
    }),
    acceptTicket: builder.mutation<any, { ticketId: number}>({
      query: ({ ticketId }) => ({
        url: `/gw/tickets/accept/${ticketId}`,
        method: "PUT"
      }),
      invalidatesTags: ['Ticket']
    }),
    rejectTicket: builder.mutation<any, { ticketId: number}>({
      query: ({ ticketId }) => ({
        url: `/gw/tickets/reject/${ticketId}`,
        method: "PUT",
      }),
      invalidatesTags: ['Ticket']
    }),
    //* GET TICKET DETAILS
    getTicketDetails: builder.query<TicketDetailsResponse, number>({
      query: (ticketId) => ({
        url: `/gw/tickets/${ticketId}`,
        method: "GET",
      }),
      transformResponse: (response: any): TicketDetailsResponse => {
        return {
          ticket: {
            id: response.ticket.ticket_id,
            title: response.ticket.ticket_title,
            description: response.ticket.ticket_description,
            createdAt: response.ticket.createdAt,
            closedAt: response.ticket.closed_at,
            company: response.ticket.company,
            type: response.ticket.ticket_type,
            category: response.ticket.category,
            subcategory: response.ticket.subcategory,
            status: response.ticket.status,
            client: {
              id: response.ticket.client.client_id,
              username: response.ticket.client.client_username,
              email: response.ticket.client.client_email,
            },
            createdBy: response.ticket.created_by_agent ? {
              id: response.ticket.created_by_agent.agent_id,
              username: response.ticket.created_by_agent.agent_username,
            } : null,
            assignedTo: response.ticket.last_assigned_agent ? {
              id: response.ticket.last_assigned_agent.agent_id,
              username: response.ticket.last_assigned_agent.agent_username,
            } : null,
            priority: response.ticket.priority_name,
            firstAcceptanceTime: response.ticket.first_acceptance_time,
          },
          notes: response.notes.map((note: any) => ({
            id: note.note_id,
            body: note.note_body,
            agent: {
              id: note.agent_id,
              username: note.agent_username,
            },
            visibleToClient: note.is_visible_to_client === 1,
          })),
          histories: response.histories.map((history: any) => ({
            id: history.ticket_history_id,
            ticketId: history.ticket_id,
            updateType: history.update_type_description,
            agent: history.assigned_agent_id ? {
              id: history.assigned_agent_id,
              username: history.assigned_agent_username,
            } : null,
            status: history.status,
            updateValue: history.update_value,
            updateReason: history.update_reason_description,
            assignmentAccepted: history.assignment_accepted === 1 ? true : 
                                 history.assignment_accepted === 0 ? false : null,
            updatedAt: history.updatedAt,
          })),
        };
      },
      providesTags: ["Ticket"],
    }),
    //* GET PENDING TICKETS
    getPendingTickets: builder.query<any, void>({
      query: () => ({
        url: "/gw/tickets/agent/pending",
        method: "get",
      }),
      providesTags: ['PendingTickets'],
    }),
    //* ADD NOTE TO TICKET
    addNote: builder.mutation<any, any>({
      query: (body) => ({
        url: "/gw/notes/create",
        method: "POST",
        body,
      }),
      invalidatesTags: ['Ticket']
    }),
  }),
});

export const { 
  useGetAllTicketsQuery, 
  useCreateTicketMutation,
  useGetTicketDetailsQuery,
  useGetPendingTicketsQuery,
  useAddNoteMutation,
  useAssignTicketMutation,
  useAcceptTicketMutation,
  useRejectTicketMutation
} = ticketsApi;
